/**
 * One place for the title, description, H1 and intro of every hand-written
 * route.
 *
 * Before this module those four strings lived twice: once in
 * scripts/prerender.cjs ROUTES (what a crawler reads) and once in the page
 * component's usePageSeo call and its visible markup (what a reader reads).
 * Three of them had already drifted apart. Now both layers read this file, so
 * the raw HTML and the mounted page cannot disagree.
 *
 * The other routes already worked this way and are not repeated here:
 *   - the seven verticals live in src/data/verticals.ts
 *   - the six comparisons live in src/data/comparisons.ts
 *   - /aeo and /guides/ai-search-visibility live in src/data/aeoProgram.ts
 *
 * Keep this a plain TypeScript module with no JSX and no runtime imports:
 * scripts/prerender.cjs transpiles it in-process with esbuild and requires it
 * as CommonJS.
 *
 * `intro` is the page's answer-first paragraph. On every indexable route it is
 * also the visible lede under the H1, and prerender stamps the same string
 * into the static fallback. On the noindex legal routes it is only the static
 * fallback: those pages open with their own effective-date line and their
 * body's first paragraph carries the defined terms.
 *
 * `h1` is the text content of the page's H1, not its markup. A component may
 * still break it across lines (see PageHeading's splitTitle in
 * src/rebuild/Shell.tsx); the text content has to stay byte-identical.
 */
export type PageSeoEntry = {
  title: string;
  description: string;
  h1: string;
  intro: string;
  noindex?: boolean;
};

export const PAGE_SEO = {
  // The home H1 and the rotating phrases are the operator's own words and are
  // fixed by GOAL.md. The answer-first paragraph goes under the page's
  // "What does Chleb AI do?" heading instead, in src/rebuild/Home.tsx and in
  // prerender's matching homepageHeroFallback().
  "/": {
    title: "Chleb AI | AI Receptionist Company in Tampa, FL",
    description:
      "Tampa-based Chleb AI offers a CRM, an AI receptionist with website chat, and a bundle connecting both. Compare plans for your service business.",
    h1: "We pick up when you’re on a job.",
    intro:
      "Chleb AI builds front-office systems for service businesses from Tampa, Florida. Choose the CRM at $297 a month, AI Receptionist plus Chatbot at $899 a month, or both connected in the Front Office Bundle at $997 a month. The receptionist answers calls around the clock and offers appointments within your configured calendar rules. The bundle connects its call data to the CRM. Usage is billed separately at carrier cost. Call (813) 694-8671 to hear the agent.",
  },
  "/book": {
    title: "Book a Demo Call With Chleb AI",
    description:
      "Pick a time and see Chleb against your own call flow. We review your services, calendar, and phone setup, and check which of your tools connect.",
    h1: "See it run against your own call flow",
    intro:
      "A live look at Chleb using your call flow. We review your current bottleneck, the services that matter, and the integrations you need, then check which of your tools connect. Booking a discovery call does not authorize work and does not start a subscription. If the calendar does not display, open it in a new tab or call (813) 694-8671.",
  },
  "/contact": {
    title: "Contact Chleb AI: Phone, Email, Tampa FL",
    description:
      "Reach Chleb AI by phone at (813) 694-8671 or email chleb@chleb.ai. Based in Tampa, Florida. Ask about call answering, CRM, websites, or booking.",
    h1: "How do I reach Chleb AI?",
    intro:
      "Call (813) 694-8671 and you reach the same AI receptionist your customers would. Email chleb@chleb.ai for anything else. Chleb Holdings LLC is based in Tampa, Florida and serves service businesses across the United States. Use the form for a question, a quote, or a scope review.",
  },
  "/about": {
    title: "Who Runs Chleb AI: Dominik Kloda and Jake Daly",
    description:
      "Chleb AI is run by founder Dominik Kloda and co-founder Jake Daly from Tampa, Florida. They build every customer call flow themselves. Here is the background.",
    h1: "Operators first. Builders by necessity.",
    intro:
      "Chleb AI started as Dominik Kloda's idea, and he builds it: the receptionist, the call flows, the CRM. Co-founder Jake Daly runs operations. They build every customer call flow themselves rather than handing it to a support queue. The company is Chleb Holdings LLC, based in Tampa, Florida, serving service businesses across the United States.",
  },
  "/solutions": {
    title: "Solutions: AI Receptionist, CRM, Websites | Chleb AI",
    description:
      "One page for everything Chleb AI sells: the AI receptionist, the CRM, missed-call recovery, the AI employee, calendar booking, websites and workflows.",
    h1: "What can Chleb AI run for my front office?",
    intro:
      "Seven things, one page. The AI receptionist answers the phone, the chatbot answers the website, the CRM holds the record, the calendar takes the booking, an unanswered call gets a text back, the workflows carry the next step, and the website captures the lead. Each section below covers one of them.",
  },
  "/pricing": {
    title: "AI Receptionist $899 a Month, CRM $297 | Chleb AI",
    description:
      "CRM $297, AI Receptionist plus Chatbot $899, Front Office Bundle $997 per month. Monthly or annual plans. Ask about availability and setup.",
    h1: "How much does an AI receptionist cost?",
    intro:
      "Chleb publishes three monthly prices. The CRM is $297 a month. The AI Receptionist plus Chatbot is $899 a month. Both together, the Front Office Bundle, is $997 a month. All three start with an inquiry to confirm availability and setup. Call minutes and emails bill separately at carrier cost to a card in your own account.",
  },
  "/faq": {
    title: "AI Receptionist FAQ: Setup, Pricing, Data | Chleb AI",
    description:
      "How the AI receptionist handles a call, what each plan costs, how fast setup runs, which tools connect, and who owns your call recordings and transcripts.",
    h1: "Common questions about the AI receptionist and CRM",
    intro:
      "Answers drawn from what the site already publishes: how a call is handled, what each plan costs, how long setup takes, which tools connect, and who owns your recordings and transcripts. Where Chleb does not publish something yet, the answer says so rather than guessing.",
  },
  "/industries": {
    title: "AI Receptionists by Industry | Chleb AI",
    description:
      "AI call answering, CRM, and booking configured for HVAC, dental, plumbing, auto dealers, salons, med spas, and lawn care. Pick your trade to see the call flow.",
    h1: "Which industries does Chleb answer calls for?",
    intro:
      "Chleb configures the call flow around your trade. There are nine industry pages: HVAC, dental, plumbing, roofing, law firms, auto dealerships, salons and barbershops, med spas, and lawn care. Each one shows a sample call, the vocabulary the agent is tuned for, and the questions owners ask before they switch.",
  },
  "/comparisons": {
    title: "AI Receptionist Alternatives Compared | Chleb AI",
    description:
      "Compare Smith.ai, Synthflow, Goodcall, Vapi, Air AI, and Ring.io with Chleb. Review product scope, billing structure, current sources, and verification limits.",
    h1: "Which AI receptionist should I choose?",
    intro:
      "Receptionist services, configurable voice platforms, and sales dialers solve different problems. These comparisons cover Smith.ai, Synthflow, Goodcall, Vapi, Air AI, and Ring.io alongside Chleb, with dated primary sources, billing structure, and clear limits on what could be verified.",
  },
  "/why-us": {
    title: "Why Service Businesses Pick Chleb | Chleb AI",
    description:
      "The founders write your call flow, run the test calls, and fix the exact scenario when a call goes wrong. Published prices and the CRM in the same bill.",
    h1: "Why pick Chleb over another AI receptionist?",
    intro:
      "Two founders build every setup. They write the call flow from your actual services and pricing, run live test calls before your number goes live, and fix the exact scenario when a call is mishandled rather than apologizing for it. The prices are published and the CRM is in the same bill. Founder Dominik Kloda and co-founder Jake Daly are hands-on with the call flow, prompts, and test calls.",
  },
  "/verifications": {
    title: "Trust & Security | Chleb AI",
    description:
      "Read about Chleb AI data safeguards, access controls, customer records, and healthcare-related agreements.",
    h1: "Clear about how your information is handled.",
    intro:
      "Customer conversations deserve care. Understand the safeguards, responsibilities, and agreements behind your setup.",
    noindex: true,
  },
  "/privacy-policy": {
    title: "Privacy Policy | Chleb AI",
    description: "Privacy Policy for Chleb AI services.",
    h1: "Privacy Policy",
    intro:
      "This Privacy Policy explains how Chleb Holdings LLC (DBA Chleb AI) and its affiliate CHLEB AI LLC collects, uses, stores, discloses, and protects information obtained through our websites, lead forms, scheduling tools, consultations, payment processes, communications, and AI-powered services.",
    noindex: true,
  },
  "/terms-of-service": {
    title: "Terms of Service | Chleb AI",
    description: "Terms of Service for Chleb AI services.",
    h1: "Terms of Service",
    intro:
      "These Terms of Service govern access to and use of services provided by Chleb Holdings LLC (DBA Chleb AI) and its affiliate CHLEB AI LLC, covering website design, calendar and scheduling configuration, workflow automation, AI receptionists and AI messaging, and ongoing system management for every visitor, client, and business entity that engages Chleb.",
    noindex: true,
  },
  "/cookie-policy": {
    title: "Cookie Policy | Chleb AI",
    description:
      "What Chleb AI stores in your browser, which third parties set it, how long it lasts, and how to change or withdraw your choice.",
    h1: "Cookie Policy",
    intro:
      "This page lists what chleb.ai puts in your browser and who puts it there. Every entry was read from a browser session against this site: the cookie jar, local storage and session storage were listed before any choice was made, after Reject, and after Accept all, and the network requests were recorded each time.",
    noindex: true,
  },
  "/security": {
    title: "Security | Chleb AI",
    description:
      "What protects the information you send Chleb AI, where it is stored, who can read it, what we do not claim, and how to report a vulnerability.",
    h1: "How your information is protected.",
    intro:
      "Written to be checked, not to reassure. Where something is not done, it says so. This page covers where the information you send us goes, what protects it in transit and at rest, what we hold no certification for, and how to report a problem.",
    noindex: true,
  },
  "/blog": {
    title: "Chleb AI Blog: Guides for Service Business Owners",
    description:
      "Plain guides for service business owners on AI receptionists, the rules on AI phone calls, websites, CRMs and getting found on Google. Every fact sourced.",
    h1: "Stories and guides for owners who live on the phone",
    intro:
      "Plain answers for owners of HVAC, dental, med spa, law and salon businesses: how an AI receptionist works, the rules on AI phone calls, what to check before you buy, and how customers find a business on Google and in AI answers. Every fact links to its source.",
  },
  "/accessibility": {
    title: "Accessibility | Chleb AI",
    description:
      "The accessibility standard Chleb AI works to, what is already in place, what we know is unfinished, and how to tell us about a barrier.",
    h1: "What works, and what does not yet.",
    intro:
      "If something on this site keeps you from doing what you came to do, tell us and we will fix it or find you another way through. This page sets out the standard we build against, what is already in place, and what we know is unfinished.",
    noindex: true,
  },
} satisfies Record<string, PageSeoEntry>;

export type PageSeoPath = keyof typeof PAGE_SEO;
